import { z } from 'zod';
import { centavos } from './envelope.ts';

/**
 * RESPONSIBLE GAMING AND PLAYER SUPPORT — the shapes a PLAYER sends about their
 * own account. (The older KYC intake was here too, until it closed on 23
 * October 2026.)
 *
 * Its own file rather than more of `lists.ts` for the reason `support.ts` gives:
 * that file describes what an OPERATOR filters, decides and closes. Everything
 * here is written by the person the record is about, from a phone, with no
 * operator involved and nobody to ask what a field means — which is why every
 * closed set below ships its LABELS beside its codes. The player portal builds
 * its pickers from these arrays, so a period the API refuses can never be a
 * period the screen offered.
 *
 * NOTHING HERE CARRIES A PLAYER ID. Every route that reads these shapes takes
 * the player from the session cookie; there is no field on any of these
 * requests by which a caller could name somebody else's account.
 */

/* ══════════════════════════════════════════════════════════════════════════
   LIMITS A PLAYER SETS ON THEMSELVES
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The three kinds the intake accepts, and why it is three rather than the four
 * `player.responsible_gaming_limit` models.
 *
 * ── WAGER IS WHY THIS FILE CHANGED ─────────────────────────────────────────
 *
 * PAGCOR form OMD/Online-0060 Rev. 2, item 24.0 (RF: p. 31 - V.1.m): "The EGS
 * along with its OGP must have a feature allowing the Player to set his/her
 * betting limits daily, weekly and/or monthly." Cell K144 of the inspection copy
 * in `pagcor compliance/` reads **DID NOT COMPLY** — the only item marked on it.
 *
 * The periods were never missing. A BETTING limit was.
 *
 * ── AND WHY THE NOTE THAT USED TO BE HERE WAS STALE ────────────────────────
 *
 * It said LOSS was "recorded and shown and not enforced" because "nothing in
 * this platform places a bet: `game.game_round` is written by the seed, the bulk
 * generator and two tests, and there is no wager path, no game launch and no
 * session."
 *
 * Every clause of that was true when written and none of it is now. SA Gaming's
 * `PlaceBet` debits a player and writes `game.game_round` through
 * `sa-callbacks.ts`, the launch route opens a real table, and
 * `playerBarredFromPlay` is already consulted on both paths. The enforcement
 * point the note was waiting for arrived and the note did not notice — which is
 * the whole reason a comment explaining why something is absent has to be read
 * again whenever the thing it describes changes.
 *
 * All three money kinds are now enforced at that point, and the portal states
 * which per kind rather than in general.
 *
 * SESSION_TIME is still not offered. It carries MINUTES AND A NULL AMOUNT — the
 * database requires that (`rg_limit_measure_matches_kind`, sql/250) — so it is
 * not a fourth money limit: the response shape, the "used" figure and the
 * portal's rendering all take a different measure, and there is still no session
 * clock to measure against. That is a product decision before it is a typed one.
 *
 * ── IF YOU ARE ADDING SESSION_TIME ────────────────────────────────────────
 *
 * Adding a kind here is not the whole job. THE READER AND THE ENFORCER BOTH
 * HAVE TO LEARN IT:
 *
 *   `services/api/src/modules/responsible-gaming/routes.ts` — `USED_IN_WINDOW`
 *   needs a measurement for it, and `readLimits` skips any kind absent from this
 *   enum so the player's screen still renders;
 *
 *   `services/api/src/modules/games/play-gate.ts` — `wagerRefusal` reads the
 *   money kinds from `ENFORCED_AT_BET`. A kind offered on the screen and absent
 *   there is a control the player can set and nothing applies.
 *
 * This paragraph exists because the gap it describes was a LIVE 500: before the
 * reader was hardened, the first WAGER or SESSION_TIME row a player held took
 * `GET /api/player/responsible-gaming` down entirely, on every load, on the one
 * screen a player uses to limit themselves.
 */
export const responsibleGamingLimitKind = z.enum(['DEPOSIT', 'LOSS', 'WAGER']);
export type ResponsibleGamingLimitKind = z.infer<typeof responsibleGamingLimitKind>;

/** The three windows, with the words the player reads. One definition, two readers. */
export const RESPONSIBLE_GAMING_LIMIT_KINDS: readonly {
  key: ResponsibleGamingLimitKind;
  label: string;
  /** What the platform does with it, stated to the player rather than implied. */
  enforcement: string;
}[] = [
  {
    key: 'DEPOSIT',
    label: 'Deposit limit',
    enforcement: 'Enforced. A deposit that would take you past it is refused.',
  },
  {
    key: 'LOSS',
    label: 'Loss limit',
    enforcement:
      'Enforced. Once what you are down for the period plus your next stake would pass it, ' +
      'the bet is refused.',
  },
  {
    key: 'WAGER',
    label: 'Betting limit',
    enforcement: 'Enforced. A bet that would take your total stakes past it is refused.',
  },
];

export const responsibleGamingLimitPeriod = z.enum(['DAILY', 'WEEKLY', 'MONTHLY']);
export type ResponsibleGamingLimitPeriod = z.infer<typeof responsibleGamingLimitPeriod>;

/**
 * The windows, described exactly as the database measures them.
 *
 * ROLLING GAMING DAYS, not calendar weeks and months, and the labels say so.
 * `player.rg_window_start` in sql/250 subtracts six or twenty-nine days from the
 * start of the current gaming day — Manila midnight, which is 16:00Z. A
 * calendar month would reset on the 1st, so somebody who reached their cap on
 * the 31st would be unlimited the next morning; a screen that said "this month"
 * over a rolling window would be describing a different rule from the one
 * being enforced.
 */
export const RESPONSIBLE_GAMING_LIMIT_PERIODS: readonly {
  key: ResponsibleGamingLimitPeriod;
  label: string;
  window: string;
}[] = [
  { key: 'DAILY', label: 'Per day', window: 'the current gaming day (resets at midnight Manila)' },
  { key: 'WEEKLY', label: 'Per week', window: 'a rolling 7 gaming days' },
  { key: 'MONTHLY', label: 'Per month', window: 'a rolling 30 gaming days' },
];

/**
 * Setting or changing one limit.
 *
 * There is no `remove` and there is no amount of zero. A limit this platform
 * accepted may be RAISED, after the cooling-off, and may not be deleted — the
 * deferred constraint trigger `trg_rg_limit_singular` refuses a transaction
 * that would leave the account with none, and the portal says so before the
 * player sets their first one.
 *
 * The amount is a STRING of centavos, like every other money field on this
 * platform's wire. A JSON number is an IEEE 754 double; ₱250.75 entered on a
 * phone and multiplied by 100 in JavaScript is 25074.999999999996.
 */
export const setResponsibleGamingLimitRequest = z
  .object({
    kind: responsibleGamingLimitKind,
    period: responsibleGamingLimitPeriod,
    amount: centavos,
  })
  .superRefine((value, ctx) => {
    /*
     * Re-checked here rather than assumed from `centavos`, for the reason
     * `applyRestrictionRequest` in lists.ts states: a field whose regex failed
     * leaves the object DIRTY rather than aborted, so this refinement is still
     * handed values the regex already rejected — and `BigInt('250.75')` does
     * not return false, it THROWS, which escapes safeParse as a 500 for what is
     * a mistyped amount.
     */
    if (!/^-?\d+$/.test(value.amount)) return;

    const amount = BigInt(value.amount);
    if (amount <= 0n) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'A limit of zero is not a limit — it is a closed account. Set an amount above ₱0.',
      });
    }
    /* ₱100,000,000 a day. Not a policy ceiling: a bound on the field, so a
       fat-fingered figure with four extra zeros is caught here rather than
       becoming a limit that can never be tightened back without a cooling-off. */
    if (amount > 10_000_000_000n) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'That is above the highest limit this platform will record (₱100,000,000).',
      });
    }
  });
export type SetResponsibleGamingLimitRequest = z.infer<typeof setResponsibleGamingLimitRequest>;

/* ══════════════════════════════════════════════════════════════════════════
   SELF-EXCLUSION
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * How long the account is locked for.
 *
 * A CLOSED SET rather than a free date, and that is the control working: a
 * player choosing their own end date can choose tomorrow, and a lock somebody
 * can end tomorrow is a pause. The shortest here is seven days.
 *
 * PERMANENT carries no expiry at all. `player.player_restriction.expires_at`
 * stays NULL, `trg_self_exclusion_lock` refuses every lift, and
 * `trg_self_exclusion_not_weakened` refuses giving it an end date afterwards —
 * so it is permanent in the database and not merely on the screen.
 */
export const selfExclusionPeriod = z.enum([
  'SEVEN_DAYS',
  'THIRTY_DAYS',
  'SIX_MONTHS',
  'ONE_YEAR',
  'PERMANENT',
]);
export type SelfExclusionPeriod = z.infer<typeof selfExclusionPeriod>;

/**
 * The periods with their length in days. `days: null` is PERMANENT.
 *
 * The DAYS are here rather than in the route because the screen has to tell the
 * player the date their lock ends BEFORE they confirm it, and a second table of
 * durations in the portal is a second thing to keep in step with the one the
 * API writes.
 */
export const SELF_EXCLUSION_PERIODS: readonly {
  key: SelfExclusionPeriod;
  label: string;
  days: number | null;
}[] = [
  { key: 'SEVEN_DAYS', label: '7 days', days: 7 },
  { key: 'THIRTY_DAYS', label: '30 days', days: 30 },
  { key: 'SIX_MONTHS', label: '6 months', days: 182 },
  { key: 'ONE_YEAR', label: '1 year', days: 365 },
  { key: 'PERMANENT', label: 'Permanently', days: null },
];

/**
 * Excluding yourself.
 *
 * `acknowledged` must be literally `true`, and the screen puts it behind a
 * checkbox the player has to tick. It is not ceremony: this is the only action
 * a player can take on this platform that NOBODY can undo — not them, not
 * support, not an administrator with a psql session — and a control that
 * irreversible must not be one mis-tap away.
 */
export const requestSelfExclusionRequest = z.object({
  period: selfExclusionPeriod,
  acknowledged: z.literal(true, {
    message: 'Confirm you understand this cannot be lifted early, by you or by anybody else.',
  }),
});
export type RequestSelfExclusionRequest = z.infer<typeof requestSelfExclusionRequest>;

/* The older KYC intake's shapes were here — a document's kind and a fingerprint,
   no file — until the owner closed that process on 23 October 2026. Identity is
   presented through `player-identity.ts` alone. The five kinds it took are still
   the `kyc_document_kind_known` CHECK in sql/250, over the rows it left. */

/* ══════════════════════════════════════════════════════════════════════════
   SUPPORT, RAISED BY THE PLAYER
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Topics a player can raise a ticket under.
 *
 * `support.ticket.topic` is a free-text varchar with no enum behind it, so this
 * list IS the enum — the same argument `agentTicketTopic` makes in support.ts,
 * and the same reason the two lists are NOT shared: a partner has a downline
 * and a payout, and a player has a deposit that did not arrive and a limit they
 * want explained. One shared list would offer each of them a topic they cannot
 * mean.
 *
 * "Responsible gaming" is on it deliberately and sits first among the account
 * topics: a player who wants to talk to somebody about their play must not have
 * to file it under "Something else".
 */
export const playerTicketTopic = z.enum([
  'Deposit problem',
  'Withdrawal problem',
  'Account or verification',
  'Game or bet dispute',
  'Responsible gaming',
  'Something else',
]);
export type PlayerTicketTopic = z.infer<typeof playerTicketTopic>;

export const createPlayerSupportTicketRequest = z.object({
  topic: playerTicketTopic,
  subject: z.string().trim().min(3, 'Give your message a subject.').max(300),
  body: z.string().trim().min(3, 'Say what happened.').max(4000),
});
export type CreatePlayerSupportTicketRequest = z.infer<typeof createPlayerSupportTicketRequest>;

export const playerSupportReplyRequest = z.object({
  body: z.string().trim().min(1, 'Write a reply.').max(4000),
});
export type PlayerSupportReplyRequest = z.infer<typeof playerSupportReplyRequest>;
